/**
 * Typed access to the Vite build-time environment.
 *
 * Replaces the `process.env.NEXT_PUBLIC_*` reads of the Next.js app. Vite
 * inlines `import.meta.env.VITE_*` at build time, so these are compile-time
 * constants in the bundle exactly as the `NEXT_PUBLIC_` ones were — the naming
 * is the only thing that changed.
 *
 * `API_PROXY` mode is gone deliberately: the apps are now served from their own
 * subdomains (app./admin.hrdocket.com) against a shared-cookie API on
 * api.hrdocket.com, so there is no same-origin rewrite to fall back to.
 *
 * The customer and admin apps are built from the same set of variables with the
 * same values. Neither is told which one it is: `selfUrl` is resolved by
 * matching the browser's origin against the two configured surfaces.
 */

type Env = {
  /** Backend origin, e.g. https://api.hrdocket.com. */
  apiUrl: string;
  /** The customer app's origin, e.g. https://app.hrdocket.com — in both apps. */
  appUrl: string;
  /** The admin app's origin, e.g. https://admin.hrdocket.com — in both apps. */
  adminUrl: string;
  /** This app's own origin. Used for OAuth/email `callbackURL`s. */
  selfUrl: string;
  /** Public marketing site (separate WordPress install on the apex domain). */
  marketingUrl: string;
  sentryDsn: string | undefined;
  sentryEnvironment: string;
  turnstileSiteKey: string | undefined;
};

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function sameOrigin(url: string, origin: string): boolean {
  try {
    return new URL(url).origin === origin;
  } catch {
    return false;
  }
}

const browserOrigin = typeof window === "undefined" ? "" : window.location.origin;

const appUrl = trimTrailingSlash(import.meta.env.VITE_APP_URL ?? "http://localhost:5173");
const adminUrl = trimTrailingSlash(import.meta.env.VITE_ADMIN_URL ?? "http://localhost:5174");

/** The configured surface this page is served from. Falls back to the browser's
 *  own origin on a host neither variable names (a preview deploy), which is
 *  still where a callback has to return to. */
const selfUrl = [appUrl, adminUrl].find((url) => sameOrigin(url, browserOrigin)) ?? browserOrigin;

export const env: Env = {
  apiUrl: trimTrailingSlash(import.meta.env.VITE_API_URL ?? "http://localhost:8787"),
  appUrl,
  adminUrl,
  selfUrl,
  marketingUrl: trimTrailingSlash(import.meta.env.VITE_MARKETING_URL ?? "https://hrdocket.com"),
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || undefined,
  sentryEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || undefined,
};
